import { AbortedRequestError, getValue, Query, QueryBuilder } from '@softline/core';
import { ApiRow } from '../types/api-row';
import {
  addDateFilter,
  addInventoryColumns,
  addPeriodColumns,
  addPeriodFilters,
  splitByPeriod,
  STATISTICS_PREFIX,
  valFromRow,
} from './document-type-query.helpers';

const NEEDS_INVOICE_MEASURE = new Set<string>();

export function resetStockOnlySupport(): void {
  NEEDS_INVOICE_MEASURE.clear();
}

const STOCK_GROUPABLE_PREFIXES = [
  'query_period',
  'product.',
  'product_group.',
  'product_main_group.',
  'product_sub_group.',
  'product_sub_sub_group.',
  'warehouse.',
  'sales_organisation.',
];

const matchesPrefix = (field: string, prefixes: readonly string[]): boolean =>
  prefixes.some((prefix) => field === prefix || field.startsWith(prefix));

function collectFilterFields(filter: unknown, into: Set<string>): void {
  if (!filter || typeof filter !== 'object') return;
  const node = filter as { name?: unknown; filters?: unknown };
  if (typeof node.name === 'string') into.add(node.name);
  if (Array.isArray(node.filters)) {
    for (const child of node.filters) collectFilterFields(child, into);
  }
}

export interface InventoryValues {
  value: number;
  reference: number;
}

export interface AbortableDataSource {
  loadAbortable(
    params: { query: Query<object> },
    signal?: AbortSignal
  ): Promise<unknown>;
}

export const INVENTORY_TOTAL_KEY = '';


export async function loadInventoryValues(
  ds: AbortableDataSource,
  base: QueryBuilder<ApiRow>,
  keyField: string | null | ((row: ApiRow) => string),
  period: { from: string; to: string },
  referencePeriod: { from: string; to: string } | null,
  options: {
    signal?: AbortSignal;
    keys?: readonly string[];
    needsInvoiceMeasure?: boolean;
  } = {}
): Promise<Map<string, InventoryValues>> {
  const { signal, keys } = options;
  const build = (withInvoiceMeasure: boolean) => {
    const builder = base.copy();
    addInventoryColumns(builder, { withComparison: false });
    if (withInvoiceMeasure) {
      builder.column(`${STATISTICS_PREFIX}.net`, { usage: 'aggregate', agg: 'sum' });
    }
    if (keys && keys.length > 0 && typeof keyField === 'string') {
      builder.filter({
        type: 'stringSet',
        name: keyField,
        values: [...keys],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);
    }
    if (referencePeriod) {
      addPeriodColumns(builder);
      addPeriodFilters(builder, period, referencePeriod);
    } else {
      addDateFilter(builder, period.from, period.to);
    }
    return builder.build();
  };

  const stockOnly = build(false);
  const groupColumns = stockOnly.columns
    .filter((c) => c.usage === 'group')
    .map((c) => String(c.name));
  const grouping = groupColumns.join(',');
  const callerFields = new Set(groupColumns);
  collectFilterFields(base.build().filters, callerFields);
  const unresolvable = [...callerFields].filter(
    (name) => !matchesPrefix(name, STOCK_GROUPABLE_PREFIXES)
  );
  const withInvoiceMeasure =
    options.needsInvoiceMeasure === true ||
    unresolvable.length > 0 ||
    NEEDS_INVOICE_MEASURE.has(grouping);
  const builtQuery = withInvoiceMeasure ? build(true) : stockOnly;
  const query = builtQuery as unknown as Query<object>;

  let result: { data?: ApiRow[] } | null;
  try {
    result = (await ds.loadAbortable({ query }, signal)) as {
      data?: ApiRow[];
    } | null;
  } catch (e) {
    if (e instanceof AbortedRequestError) throw e;
    if (!withInvoiceMeasure) {
      NEEDS_INVOICE_MEASURE.add(grouping);
      console.warn(
        '[sales-statistics] Lagerwert needs the combined path for this grain — retrying',
        { grouping, response: (e as { error?: unknown })?.error }
      );
      return loadInventoryValues(ds, base, keyField, period, referencePeriod, {
        ...options,
        needsInvoiceMeasure: true,
      });
    }
    console.error('[sales-statistics] Lagerwert query rejected', {
      error: e,
      response: (e as { error?: unknown })?.error,
      status: (e as { status?: unknown })?.status,
      withComparison: !!referencePeriod,
      grouping,
      aggregates: builtQuery.columns
        .filter((c) => c.usage === 'aggregate')
        .map((c) => `${String(c.name)}:${(c as { agg?: string }).agg ?? ''}`),
      payload: JSON.stringify(query),
    });
    throw e;
  }

  const rows = result?.data ?? [];
  const keyOf = (row: ApiRow): string => {
    if (typeof keyField === 'function') return keyField(row);
    return keyField ? String(getValue(row, keyField) ?? '') : INVENTORY_TOTAL_KEY;
  };

  if (!referencePeriod) {
    const values = new Map<string, InventoryValues>();
    for (const row of rows) {
      if (!row) continue;
      const value = valFromRow(row, 'inventory_value');
      values.set(keyOf(row), { value, reference: value });
    }
    return values;
  }

  const { current, previous } = splitByPeriod(rows);
  const values = new Map<string, InventoryValues>();
  for (const row of current) {
    if (!row) continue;
    values.set(keyOf(row), {
      value: valFromRow(row, 'inventory_value'),
      reference: 0,
    });
  }
  for (const row of previous) {
    if (!row) continue;
    const key = keyOf(row);
    const reference = valFromRow(row, 'inventory_value');
    const existing = values.get(key);
    if (existing) existing.reference = reference;
    else values.set(key, { value: 0, reference });
  }
  return values;
}

export async function resolveInventoryValues(
  cache: Map<string, Promise<Map<string, InventoryValues>>>,
  cacheKey: string,
  load: () => Promise<Map<string, InventoryValues>>
): Promise<Map<string, InventoryValues> | null> {
  const pending =
    cache.get(cacheKey) ??
    load().catch((e) => {
      cache.delete(cacheKey);
      throw e;
    });
  cache.set(cacheKey, pending);
  try {
    return await pending;
  } catch {
    return null;
  }
}

export interface InventoryRowFields {
  inventoryValue: number;
  referenceInventoryValue: number;
  minInventoryValue: number;
  inventoryDeltaPos: number;
  inventoryDeltaNeg: number;
}

export function withInventoryValues<T extends InventoryRowFields>(
  row: T,
  values: InventoryValues | undefined
): T {
  const value = values?.value ?? 0;
  const reference = values?.reference ?? 0;
  return {
    ...row,
    inventoryValue: value,
    referenceInventoryValue: reference,
    minInventoryValue: Math.min(value, reference),
    inventoryDeltaPos: reference < value ? value - reference : 0,
    inventoryDeltaNeg: reference > value ? reference - value : 0,
  };
}
